import { useMemo } from 'react'

import {
  useAttributeOptions,
  useGroupedAttributeOptions,
  useSegmentBy
} from 'redux/hooks/AttributeOptions'

import type { TAttributeOptionDto } from '@homepal/api-contract'

import { GroupedAttributeOptions } from 'types/GlobalAttributeOptions'
import { isCustomEconomyMetric } from 'types/GlobalMetric'
import { IMetric, MetricStatus } from 'types/GlobalMetric'
import { TCoreWidgetSettingsLoaded, WidgetType } from 'types/GlobalWidget'

import { hasVariantOptions } from 'utils/variantOptions'

/**
 * Validate availability of metric based on
 * widgets active segment and filters
 * @param widget - Active widget
 * @param factTableId - Fact table of metric
 * @param segmentBy - Widget segment by
 * @param groupedAttributeOptions - attribute options per fact table.
 * @param activeFilterRelationsKey - Current filter relation keys on widget
 * @returns
 */
const validateMetricSettings = (
  widget: TCoreWidgetSettingsLoaded,
  metric: IMetric,
  segmentBy: TAttributeOptionDto | null,
  groupedAttributeOptions: GroupedAttributeOptions,
  activeFilterAttributeIds: string[]
): boolean => {
  // with variant options on the widget, only custom economy metrics
  // can be added
  if (hasVariantOptions(widget.settings) && !isCustomEconomyMetric(metric)) {
    return false
  }

  if (
    widget.type === WidgetType.LINE_CHART ||
    widget.type === WidgetType.KEY_FIGURE
  ) {
    return true
  }

  if (!segmentBy) {
    // all should have segment by from here, safety check
    return false
  }

  // use fact table from any variant
  const factTableId = isCustomEconomyMetric(metric)
    ? metric.variants[0]?.fact_table_id
    : metric.fact_table_id

  if (!factTableId) return false

  const availableAttributeOptions = groupedAttributeOptions[factTableId]

  if (!availableAttributeOptions) {
    return false
  }

  const segmentAttributeIds = availableAttributeOptions.segment.map(
    (item) => item.attribute_id
  )
  const filterAttributeIds = availableAttributeOptions.filter.map(
    (item) => item.attribute_id
  )

  return (
    // segment exists
    segmentAttributeIds.includes(segmentBy.attribute_id) &&
    // all filters exists
    activeFilterAttributeIds.every((attributeId) =>
      filterAttributeIds.includes(attributeId)
    )
  )
}

/**
 * Retreive available metrics.
 * @param metrics - All metrics
 * @param displayInactiveMetrics - show inactive metrics
 * @param widget - If adding metric to widget
 */
export const useAvailableMetrics = (
  metrics: IMetric[],
  displayInactiveMetrics: boolean,
  widget: TCoreWidgetSettingsLoaded | null
): IMetric[] => {
  const attributeOptions = useAttributeOptions()
  const groupedAttributeOptions = useGroupedAttributeOptions()
  const segmentBy = useSegmentBy(widget?.settings ?? null)

  // Memoize availableMetrics
  const availableMetrics = useMemo(
    () =>
      metrics.filter(
        (metric) =>
          displayInactiveMetrics || metric.status == MetricStatus.ACTIVE
      ),
    [metrics, displayInactiveMetrics]
  )

  // Memoize filteredAttributeIds
  const filteredAttributeIds = useMemo(() => {
    if (!widget) return []

    return widget.settings.filters
      .filter(
        (filter) =>
          filter.value.selected &&
          filter.attribute_option.selected &&
          attributeOptions[filter.attribute_option.selected]
      )
      .map(
        (filter) =>
          attributeOptions[filter.attribute_option.selected as string]
            .attribute_id
      )
  }, [widget, attributeOptions])

  // Final available metrics
  return useMemo(() => {
    if (!widget) {
      return availableMetrics
    }

    return availableMetrics.filter((metric) =>
      validateMetricSettings(
        widget,
        metric,
        segmentBy,
        groupedAttributeOptions,
        filteredAttributeIds
      )
    )
  }, [
    availableMetrics,
    widget,
    segmentBy,
    groupedAttributeOptions,
    filteredAttributeIds
  ])
}
